/**
 * Spectator-side debug overlay. Shows the rolling log of incoming wire
 * frames so we can tell whether (a) the producer is publishing events at
 * all, (b) the DO is fanning them out, and (c) the spectator is receiving
 * + classifying them. Temporary debugging aid — remove once spectator UX
 * is stable.
 *
 * Fixed-position black box on the right side. Tap to dismiss.
 */

export interface SpectatorDebugLog {
  pushFrame(kind: string, info?: string): void;
  pushEvent(seq: number, eventType: string, latency?: { cs: number; sw: number }): void;
  setPhase(phase: 'catchup' | 'booting' | 'live' | 'replay' | 'ended'): void;
  destroy(): void;
}

const MAX_LINES = 14;

// No-op log returned when ?debug isn't set, so the many dbg.* call sites in
// bootWatchingMode can stay unconditional.
const NOOP_LOG: SpectatorDebugLog = {
  pushFrame() {},
  pushEvent() {},
  setPhase() {},
  destroy() {},
};

export function mountSpectatorDebugOverlay(): SpectatorDebugLog {
  // Gated behind ?debug in the URL (same flag as the producer overlay and the
  // craps hit-zone strokes).
  if (typeof window === 'undefined' || !new URLSearchParams(window.location.search).has('debug')) {
    return NOOP_LOG;
  }

  const el = document.createElement('div');
  el.style.cssText = `
    position: fixed; top: 8px; right: 8px;
    background: rgba(0,0,0,0.88); color: #0f0;
    font-family: monospace; font-size: 10px;
    padding: 6px 8px; border-radius: 6px;
    z-index: 99999; max-width: 280px; min-width: 200px;
    white-space: pre; line-height: 1.35;
    pointer-events: auto; cursor: pointer;
    overflow: hidden;
  `;
  el.title = 'Tap to hide';
  document.body.appendChild(el);

  const lines: string[] = [];
  let phase: 'catchup' | 'booting' | 'live' | 'replay' | 'ended' = 'catchup';
  let frameCount = 0;
  let eventCount = 0;
  let lastFrameAt = 0;
  // Last live-frame latencies (c→s = producer→DO, s→w = DO→spectator).
  // Header shows the most recent so you can glance at it without scanning
  // the event log. Undefined until the first live frame arrives.
  let lastCs: number | undefined;
  let lastSw: number | undefined;

  const render = () => {
    const lat = lastSw !== undefined ? `  c→s ${lastCs} s→w ${lastSw}` : '';
    const header = `phase: ${phase}  frames: ${frameCount}  events: ${eventCount}${lat}`;
    el.textContent = [header, '─'.repeat(28), ...lines.slice(-MAX_LINES)].join('\n');
  };

  // mm:ss.fff — high-resolution local time so wire-side gaps show up
  // as multi-second deltas, not as "same minute, who knows."
  const ts = (): string => {
    const d = new Date();
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    const ms = String(d.getMilliseconds()).padStart(3, '0');
    return `${mm}:${ss}.${ms}`;
  };

  el.addEventListener('click', () => { el.remove(); });

  render();

  return {
    pushFrame(kind, info) {
      frameCount += 1;
      const now = performance.now();
      const delta = lastFrameAt === 0 ? 0 : Math.round(now - lastFrameAt);
      lastFrameAt = now;
      // +Nms = gap since previous frame. Tight bursts look like +0ms,
      // +1ms, +2ms; wire-side stalls light up as +9000ms+.
      lines.push(`${ts()} +${delta}ms ${kind}${info ? ' ' + info : ''}`);
      while (lines.length > MAX_LINES * 2) lines.shift();
      render();
    },
    pushEvent(seq, eventType, latency) {
      eventCount += 1;
      if (latency) {
        lastCs = latency.cs;
        lastSw = latency.sw;
      }
      const latStr = latency ? `  c→s ${latency.cs} s→w ${latency.sw}` : '';
      lines.push(`  └─ #${seq} ${eventType}${latStr}`);
      while (lines.length > MAX_LINES * 2) lines.shift();
      render();
    },
    setPhase(p) {
      phase = p;
      render();
    },
    destroy() {
      el.remove();
    },
  };
}
